import { defineStore } from 'pinia';
import {
  paginatedFeedOnContainerScroll,
  paginatedFeedRecoverIfStuck,
} from '@/domain/market/paginated-feed-core.js';
import { runPaginatedFeedFetchPage, runPaginatedFeedLoadMore } from '@/stores/feed-fetch-error.js';
import { shouldIgnoreFeedScrollLoad } from '@/composables/feed-viewport.js';
import {
  cancelSportsContinuation,
  continueSportsFeed,
  createSportFeedFetchOptions,
  hasSportsContinuation,
} from '@/domain/sport/sport-feed-adapter.js';
import { paginatedFeedIsManualLoadRequired } from '@/domain/market/paginated-feed-pagination.js';

const sportFetchOptions = createSportFeedFetchOptions();

/**
 * Sport live Feed 状态源（paginated-feed-core + sport-feed-adapter）。
 * 视图层保留 SportMatchRow；滚动由 FeedScrollRoot + usePaginatedFeedPage 驱动。
 */
export const useSportFeedStore = defineStore('sportFeed', {
  state: () => ({
    initialized: false,
    sportsSlug: '',
    rawRows: [],
    listLoading: false,
    loadingMore: false,
    hasMore: true,
    pageNum: 0,
    sportsNextOffset: 0,
    sportsNextCursor: '',
    sportsFirstPageIds: [],
    sportsFeedModern: false,
    sportsPendingContinuation: false,
    sportsContinuationLoading: false,
    sportsPageLoading: false,
    sportsRetryAt: 0,
    sportsRefreshActive: false,
    loadGeneration: 0,
    reloadToken: 0,
    lastFetchPlatform: 'pc',
    autoAppendCount: 0,
    appendAutoLoadBlocked: false,
    listFetchError: null,
  }),

  getters: {
    showListEmpty(state) {
      return (
        !state.listLoading &&
        !state.loadingMore &&
        !state.sportsContinuationLoading &&
        !hasSportsContinuation(state) &&
        state.rawRows.length === 0
      );
    },
    manualLoadRequired(state) {
      return paginatedFeedIsManualLoadRequired(state);
    },
  },

  actions: {
    setLastFetchPlatform(isH5) {
      this.lastFetchPlatform = isH5 ? 'h5' : 'pc';
    },

    clear() {
      cancelSportsContinuation(this);
      this.loadGeneration += 1;
      this.sportsSlug = '';
      this.rawRows = [];
      this.hasMore = false;
      this.pageNum = 0;
      this.sportsNextOffset = 0;
      this.sportsNextCursor = '';
      this.sportsFirstPageIds = [];
      this.sportsFeedModern = false;
      this.sportsPendingContinuation = false;
      this.sportsPageLoading = false;
      this.sportsRetryAt = 0;
      this.listLoading = false;
      this.loadingMore = false;
      this.initialized = false;
      this.appendAutoLoadBlocked = false;
      this.listFetchError = null;
    },

    async setSportsSlug(slug) {
      const next = String(slug ?? '').trim();
      if (next === this.sportsSlug && (this.initialized || this.listLoading)) return;
      cancelSportsContinuation(this);
      this.loadGeneration += 1;
      this.listLoading = false;
      this.loadingMore = false;
      this.rawRows = [];
      this.sportsNextOffset = 0;
      this.sportsNextCursor = '';
      this.sportsFirstPageIds = [];
      this.sportsFeedModern = false;
      this.sportsPendingContinuation = false;
      this.sportsPageLoading = false;
      this.sportsRetryAt = 0;
      this.sportsSlug = next;
      this.reloadToken += 1;
      this.initialized = false;
      await this.fetchPage(false);
    },

    recoverIfStuck() {
      if (hasSportsContinuation(this) || this.sportsContinuationLoading) return;
      paginatedFeedRecoverIfStuck(this);
    },

    async ensureInit(sportsSlug) {
      if (sportsSlug !== undefined && sportsSlug !== null) {
        this.sportsSlug = String(sportsSlug).trim();
      }
      if (this.initialized && this.rawRows.length) return;
      this.recoverIfStuck();
      if (this.initialized && this.rawRows.length) return;
      await this.fetchPage(false);
    },

    async fetchPage(append = false, options = {}) {
      if (
        this.sportsContinuationLoading ||
        this.sportsPageLoading ||
        this.listLoading ||
        this.loadingMore
      )
        return false;
      if (hasSportsContinuation(this)) return this.continueSportsFeed(options);
      this.sportsPageLoading = true;
      const generation = this.loadGeneration + 1;
      let applied = false;
      try {
        await runPaginatedFeedFetchPage(this, append, {
          ...sportFetchOptions,
          ...options,
          applyPageResult: (state, result) => {
            sportFetchOptions.applyPageResult(state, result);
            applied = true;
          },
        });
      } finally {
        if (generation === this.loadGeneration) this.sportsPageLoading = false;
      }
      if (generation !== this.loadGeneration || options.signal?.aborted) return false;
      if (!applied || this.listFetchError) {
        this.sportsRetryAt = Date.now() + 30000;
        return false;
      }
      if (options.recoverEmpty !== false && hasSportsContinuation(this)) {
        return this.continueSportsFeed({ ...options, maxPages: 1 });
      }
      return true;
    },

    async loadMore(options = {}) {
      if (this.sportsContinuationLoading || this.sportsPageLoading) return;
      if (!options.manual && Date.now() < this.sportsRetryAt) return;
      if (hasSportsContinuation(this)) return this.continueSportsFeed(options);
      const generation = this.loadGeneration + 1;
      await runPaginatedFeedLoadMore(this, {
        ...sportFetchOptions,
        ...options,
      });
      if (generation === this.loadGeneration && hasSportsContinuation(this)) {
        await this.continueSportsFeed({ ...options, maxPages: 1 });
      }
    },

    continueSportsFeed(options = {}) {
      return continueSportsFeed(this, options);
    },

    cancelSportsContinuation() {
      cancelSportsContinuation(this);
    },

    retrySportsFeed() {
      this.sportsRetryAt = 0;
      return hasSportsContinuation(this)
        ? this.continueSportsFeed({ manual: true })
        : this.fetchPage(false);
    },

    onContainerScroll(container, options = {}) {
      if (this.sportsContinuationLoading || this.sportsPageLoading) return;
      if (hasSportsContinuation(this) || Date.now() < this.sportsRetryAt) return;
      paginatedFeedOnContainerScroll(this, container, {
        ...options,
        fetchOptions: sportFetchOptions,
        shouldIgnoreScrollLoad: shouldIgnoreFeedScrollLoad,
      });
    },
  },
});
